import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'

import type { ServiceStatus } from '@/lib/schemas/db'
import { StatusCard, latestStatusReason } from '@/components/StatusCard'

type CalloutStatus = ServiceStatus

interface BillableItem {
  id: string
  description: string
  quantity?: string
  photoUrl?: string
}

interface TodoItem {
  id: string
  text: string
  done?: boolean
}

import { AssignUserDialog } from '@/components/AssignUserDialog'
import {
  CalloutNotesSection,
  type CalloutNotesSectionNote,
} from '@/components/CalloutNotesSection'
import { ChangeStatusDialog } from '@/components/ChangeStatusDialog'
import { statusRequiresReason } from '@/db/domain/statusRules'
import { StatusHistoryPill } from '@/components/StatusHistoryPill'
import type { StatusChangeLog } from '@/lib/schemas/db'
import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { RichMarkdownEditor } from '@/components/RichMarkdownEditor'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import type {
  PhotoNoteReplace,
  PhotoNoteSubmit,
} from '@/components/PhotoNoteComposer'

export type CalloutLogWorkNoteKind = 'Note' | 'PhotoNote'
export type CalloutLogWorkNewNote = {
  id: string
  createdAt?: string
}
export type CalloutLogWorkNotePatch = {
  text?: string
  caption?: string
  photoUrl?: string
  photoFile?: File
}
export type CalloutLogWorkPhotoNoteInput = {
  /** The group's images, display order. A single-photo note has one entry. */
  photoFiles: File[]
  caption: string
  /** Data URLs matching photoFiles, for optimistic display. */
  photoUrls: string[]
}

export interface CalloutLogWorkScreenProps {
  id: string
  publicId?: string
  resourceLabel?: string
  showInvoiceLink?: boolean
  invoiceRoute?: 'callout' | 'session'
  customerName: string
  customerId?: string
  customerPublicId?: string
  issueSummary: string
  status: CalloutStatus
  scheduledAt: string
  address: string
  contactName: string
  contactPhone: string
  billables: BillableItem[]
  notes: CalloutNotesSectionNote[]
  statusChanges?: StatusChangeLog[]
  getAuthorDisplayName?: (authorId: string) => string
  todos: TodoItem[]
  showHeader?: boolean
  /** Optional author name to use for new notes; defaults to tech.jane in the prototype. */
  newNoteAuthorName?: string
  /** Optional callback when a new text note is added. */
  onAddTextNote?: (text: string) => CalloutLogWorkNewNote | void
  /** Optional callback when a new photo note is added. */
  onAddPhotoNote?: (
    input: CalloutLogWorkPhotoNoteInput,
  ) => CalloutLogWorkNewNote | void
  /** Optional error message to show when a photo note save fails. */
  photoNoteError?: string | null
  /** Optional callback to clear the photo note error. */
  onClearPhotoNoteError?: () => void
  /** Optional id of a photo note to remove after a failed save. */
  photoNoteRollbackId?: string | null
  /** Abstract hook when a new note is added (text or photo). */
  onNewNote?: (kind: CalloutLogWorkNoteKind) => void
  /** Abstract hook when an existing note is edited. */
  onEditNote?: (id: string, patch: CalloutLogWorkNotePatch) => void
  /** Abstract hook when a note is deleted. */
  onDeleteNote?: (id: string) => void
  /** Technicians offered in the Assign dialog. */
  users?: Array<{ id: string; name: string }>
  assignedUserId?: string
  assignedUserName?: string
  /** Assign the service to a technician (same friction as status changes). */
  onAssign?: (userId: string) => void | Promise<void>
  /**
   * Statuses the service can move to; shown in the Change-status dialog.
   * Cancelled is excluded there — cancelling has its own destructive button.
   */
  availableStatusTransitions?: CalloutStatus[]
  onChangeStatus?: (
    status: CalloutStatus,
    reason?: string,
    /** UTC ISO when the event really happened; undefined = it happened now. */
    occurredAt?: string,
  ) => void | Promise<void>
  /** Cancel the Callout. */
  onCancelCallout?: (reason?: string) => void | Promise<void>
}

export function CalloutLogWorkScreen(props: CalloutLogWorkScreenProps) {
  const {
    customerName,
    customerId,
    customerPublicId,
    issueSummary,
    status,
    scheduledAt,
    notes: initialNotes,
    statusChanges = [],
    getAuthorDisplayName,
    showHeader = true,
    newNoteAuthorName = 'tech.jane',
    onAddTextNote,
    onAddPhotoNote,
    photoNoteError,
    onClearPhotoNoteError,
    photoNoteRollbackId,
    onNewNote,
    onEditNote,
    onDeleteNote,
    onCancelCallout,
    users = [],
    assignedUserId,
    assignedUserName,
    onAssign,
    availableStatusTransitions,
    onChangeStatus,
    resourceLabel = 'Callout',
  } = props

  const [notes, setNotes] = useState<CalloutNotesSectionNote[]>(initialNotes)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)

  // Live data (new status-change events, notes from other devices) replaces
  // the local optimistic copy whenever the container passes a fresh list.
  useEffect(() => {
    setNotes(initialNotes)
  }, [initialNotes])


  const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState<string | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isCancelCalloutDialogOpen, setIsCancelCalloutDialogOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false)
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false)
  const [isCancelPending, setIsCancelPending] = useState(false)

  const runCancelAction = async (action?: (reason?: string) => void | Promise<void>, reason?: string) => {
    if (!action || isCancelPending) return
    setIsCancelPending(true)
    try {
      await Promise.resolve(action(reason))
    } finally {
      setIsCancelPending(false)
    }
  }

  const handleStartEditNote = (id: string) => {
    setEditingNoteId(id)
  }

  const handleCancelEditNote = () => {
    setEditingNoteId(null)
  }

  const handleSaveEditNote = (id: string, textOrCaption: string) => {
    const targetNote = notes.find((note) => note.id === id)
    const isPhotoNote = targetNote?.kind === 'photo'
    const patch = isPhotoNote
      ? { caption: textOrCaption }
      : { text: textOrCaption }

    setNotes((prev) =>
      prev.map((note) => {
        if (note.id !== id) return note
        if (note.kind === 'photo') {
          return {
            ...note,
            caption: textOrCaption,
          }
        }
        return {
          ...note,
          text: textOrCaption,
        }
      }),
    )
    setEditingNoteId(null)
    if (onEditNote && targetNote) {
      onEditNote(id, patch)
    }
  }

  const handleAddTextNote = (text: string) => {
    if (!text) return false

    const createdNote = onAddTextNote ? onAddTextNote(text) : undefined

    setNotes((prev) => {
      const nextIndex = prev.length + 1
      const createdAt = createdNote?.createdAt
        ? createdNote.createdAt
        : new Date().toISOString()
      const newNote: CalloutNotesSectionNote = {
        id: createdNote?.id ?? `n${nextIndex}`,
        authorName: newNoteAuthorName,
        createdAt,
        text,
      }
      return [...prev, newNote]
    })

    if (onNewNote) {
      onNewNote('Note')
    }

    return true
  }

  // Photo capture/crop/compression lives in the shared PhotoNoteComposer
  // (rendered inside CalloutNotesSection); these handlers just persist the
  // finished image and keep the local optimistic list in sync.
  const handleSubmitPhoto = ({ photoFiles, caption, photoUrls }: PhotoNoteSubmit) => {
    const createdNote = onAddPhotoNote
      ? onAddPhotoNote({ photoFiles, caption, photoUrls })
      : undefined

    setNotes((prev) => {
      const nextIndex = prev.length + 1
      const createdAt = createdNote?.createdAt
        ? createdNote.createdAt
        : new Date().toISOString()
      const newNote: CalloutNotesSectionNote = {
        id: createdNote?.id ?? `n${nextIndex}`,
        authorName: newNoteAuthorName,
        createdAt,
        kind: 'photo',
        photoUrl: photoUrls[0] ?? '',
        photoUrls,
        caption,
      }
      return [...prev, newNote]
    })

    if (onNewNote) {
      onNewNote('PhotoNote')
    }
  }

  const handleReplacePhotoImage = (
    id: string,
    { photoFile, photoUrl }: PhotoNoteReplace,
  ) => {
    setNotes((prev) =>
      prev.map((note) =>
        note.id === id && note.kind === 'photo'
          ? { ...note, photoUrl }
          : note,
      ),
    )
    if (onEditNote) {
      onEditNote(id, { photoFile, photoUrl })
    }
  }

  useEffect(() => {
    if (!photoNoteRollbackId) return
    setNotes((prev) => prev.filter((note) => note.id !== photoNoteRollbackId))
    setEditingNoteId((current) =>
      current === photoNoteRollbackId ? null : current,
    )
  }, [photoNoteRollbackId])

  const handleRequestDeleteNote = (id: string) => {
    setPendingDeleteNoteId(id)
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmDeleteNote = () => {
    if (!pendingDeleteNoteId) return

    setNotes((prev) => prev.filter((note) => note.id !== pendingDeleteNoteId))
    if (editingNoteId === pendingDeleteNoteId) {
      setEditingNoteId(null)
    }

    if (onDeleteNote) {
      onDeleteNote(pendingDeleteNoteId)
    }

    setPendingDeleteNoteId(null)
    setIsDeleteDialogOpen(false)
  }

  const handleCancelDeleteNote = () => {
    setPendingDeleteNoteId(null)
    setIsDeleteDialogOpen(false)
  }

  // Notes stay open even on a locked service: they are audited rather than
  // frozen, so what protects the record is that edits keep their history and
  // deletes keep their content. The lock only closes the record's own fields,
  // which are amended through addendums instead. The feed marks each locked
  // period itself, derived from the status history rather than current status,
  // so reopening a record does not erase that it was ever closed.
  const isLocked = status === 'Invoiced' || status === 'Cancelled'

  // Cancelling appears in the Change-status list, because that is where people
  // look for it — it was previously behind a separate button and read as
  // impossible. What keeps it deliberate is the dialog: it demands a reason and
  // its confirm turns red. Blocked → Blocked stays in the list so the blocked
  // reason can be updated.
  const moveOptions = availableStatusTransitions ?? []
  const showChangeStatusButton = Boolean(onChangeStatus) && moveOptions.length > 0
  const showCancelButton = Boolean(onCancelCallout) && !isLocked
  // On top of the domain rule (Blocked, Cancelled, unsuccessful collection),
  // leaving a locked status is deliberate: the office needs to know why.
  const moveRequiresReason = (target: CalloutStatus) =>
    statusRequiresReason(target) || isLocked

  const content = (
    <section className="space-y-4">
      <div className="grid gap-4 md:grid-cols-[2fr,1.5fr]">
        <div className="space-y-4">
          <StatusCard
            resourceLabel={resourceLabel}
            status={status}
            locked={isLocked}
            statusReason={latestStatusReason(statusChanges, status)}
            onOpenChangeStatus={
              showChangeStatusButton ? () => setIsMoveDialogOpen(true) : undefined
            }
            onOpenCancel={
              showCancelButton
                ? () => {
                    setCancelReason('')
                    setIsCancelCalloutDialogOpen(true)
                  }
                : undefined
            }
            cancelPending={isCancelPending}
            assigned={
              onAssign
                ? {
                    name: assignedUserName,
                    onOpenAssign: () => setIsAssignDialogOpen(true),
                  }
                : undefined
            }
          />
        </div>

      {/* Notes/Issue column */}
      <div className="space-y-4">
        <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
          <div className="space-y-1 text-xs">
            <FormCapsLabel>Issue</FormCapsLabel>
          </div>
          <div className="space-y-3">
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
              {issueSummary}
            </div>

            <div className="space-y-3">
              {photoNoteError ? (
                <p role="alert" className="text-xs text-red-600 whitespace-pre-line">
                  {photoNoteError}
                </p>
              ) : null}
              <CalloutNotesSection
                notes={notes}
                newNoteAuthorName={newNoteAuthorName}
                editingNoteId={editingNoteId}
                onStartEditNote={handleStartEditNote}
                onCancelEditNote={handleCancelEditNote}
                onSaveEditNote={handleSaveEditNote}
                onAddNote={handleAddTextNote}
                onSubmitPhotoNote={handleSubmitPhoto}
                onReplacePhotoNote={handleReplacePhotoImage}
                photoError={photoNoteError}
                onClearPhotoError={onClearPhotoNoteError}
                onDeleteNote={handleRequestDeleteNote}
              />
            </div>
          </div>
        </div>

        <Dialog
          open={isDeleteDialogOpen}
          onOpenChange={(open) => {
            setIsDeleteDialogOpen(open)
            if (!open) {
              setPendingDeleteNoteId(null)
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete note?</DialogTitle>
              <DialogDescription>
                This will remove the note from the {resourceLabel}. You won&apos;t see it on future screens like invoicing.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                onClick={handleCancelDeleteNote}
              >
                Keep note
              </button>
              <button
                type="button"
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                onClick={handleConfirmDeleteNote}
              >
                Delete note
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ChangeStatusDialog
          open={isMoveDialogOpen}
          refLabel={props.publicId ?? props.id}
          contextLabel={customerName}
          currentStatus={status}
          options={moveOptions}
          requiresReason={moveRequiresReason}
          onClose={() => setIsMoveDialogOpen(false)}
          onConfirm={async (nextStatus, reason, occurredAt) => {
            await onChangeStatus?.(nextStatus, reason || undefined, occurredAt)
          }}
        />

        <AssignUserDialog
          open={isAssignDialogOpen}
          refLabel={props.publicId ?? props.id}
          contextLabel={customerName}
          users={users}
          currentUserId={assignedUserId}
          onClose={() => setIsAssignDialogOpen(false)}
          onConfirm={async (userId) => {
            await onAssign?.(userId)
          }}
        />

        <Dialog
          open={isCancelCalloutDialogOpen}
          onOpenChange={(open) => {
            setIsCancelCalloutDialogOpen(open)
            if (!open) {
              setCancelReason('')
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel this {resourceLabel}?</DialogTitle>
              <DialogDescription>
                Cancelled {resourceLabel}s will disappear from the bookings list. You can add a short note so the office knows why it was cancelled.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 pt-2">
              <FormCapsLabel>Reason (required)</FormCapsLabel>
              {/* Keyed on the dialog session: tiptap owns its document, so
                  clearing `cancelReason` alone would leave stale text. */}
              <RichMarkdownEditor
                key={String(isCancelCalloutDialogOpen)}
                initialValue=""
                onChange={setCancelReason}
                placeholder="Required. E.g. client rescheduled, site access issues…"
                ariaLabel="Cancellation reason"
              />
            </div>
            <DialogFooter>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                disabled={isCancelPending}
                onClick={() => {
                  setIsCancelCalloutDialogOpen(false)
                  setCancelReason('')
                }}
              >
                Keep {resourceLabel}
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!cancelReason.trim() || isCancelPending}
                onClick={async () => {
                  const reason = cancelReason.trim()
                  if (!reason) return
                  await runCancelAction(onCancelCallout, reason)
                  setIsCancelCalloutDialogOpen(false)
                  setCancelReason('')
                }}
              >
                {isCancelPending ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" /> : null}
                Cancel {resourceLabel}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      </div>
    </section>
  )

  if (!showHeader) {
    return content
  }

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="max-w-container mx-auto px-4 py-6 space-y-4">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold sm:text-2xl">{resourceLabel} Log Work</h1>
            <p className="text-sm text-slate-600">
              {customerId ? (
                <Link
                  to="/customers/$id"
                  params={{ id: customerPublicId ?? customerId }}
                  className="text-blue-700 hover:underline"
                >
                  {customerName}
                </Link>
              ) : (
                customerName
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-start gap-2 sm:justify-end">
            <StatusHistoryPill
              currentStatus={status}
              statusChanges={statusChanges}
              getAuthorDisplayName={getAuthorDisplayName}
            />
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs text-slate-700 sm:py-1">
              Scheduled: {scheduledAt}
            </span>
            <button
              type="button"
              className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 sm:px-3 sm:py-1.5 sm:text-xs"
            >
              Modify {resourceLabel}
            </button>
          </div>
        </header>

        {content}
      </main>
    </div>
  )
}

export const demoCallout: CalloutLogWorkScreenProps = {
  id: 'c1',
  customerName: 'John Doe',
  issueSummary: 'Investigate power outage in office block',
  status: 'Arrived',
  scheduledAt: '2025-01-10 09:00',
  address: '123 Main Road, Business Park, Unit 4B',
  contactName: 'Jane Smith',
  contactPhone: '+1 555 123 4567',
  billables: [
    {
      id: 'b1',
      description: 'Callout fee (1st hour)',
      quantity: '1 unit',
    },
    {
      id: 'b2',
      description: 'Extra distance (15km)',
      quantity: '15 km',
    },
    {
      id: 'b3',
      description: 'Distribution board breaker',
      quantity: '2 units',
      photoUrl: '/1200x800.svg',
    },
  ],
  notes: [
    {
      id: 'n1',
      text: 'Client reports intermittent outages, starting last night.',
      authorName: 'tech.jane',
      createdAt: '2025-01-10 09:15',
    },
    {
      id: 'n2',
      text: 'Main breaker room located in basement, access via rear entrance.',
      authorName: 'tech.jane',
      createdAt: '2025-01-10 09:22',
    },
    {
      id: 'n3',
      kind: 'photo',
      authorName: 'tech.jane',
      createdAt: '2025-01-10 09:30',
      photoUrl: '/1200x800.svg',
      caption: 'Serial number plate on router installed during this visit.',
    },
  ],
  todos: [
    { id: 't1', text: 'Check main incoming breaker', done: true },
    { id: 't2', text: 'Confirm earth leakage test results', done: false },
  ],
}
